/**
 * AddRecommendationDialog Component
 *
 * Dialog for adding a recommendation to an inspection item with auto-cost estimation.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useCentsInput } from '@/hooks/useCentsInput';
import { formatCentsAsEuros } from '@/utils/money';
import { vatCentsFromNet } from '@/utils/vat';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningIcon from '@mui/icons-material/Warning';
import {
  useEstimateCost,
  type CostEstimate,
  type RecommendationUrgency,
  type InspectionRecommendation,
} from '@/hooks/useInspections';

interface AddRecommendationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (recommendation: {
    description: string;
    customerDescription?: string;
    urgency: RecommendationUrgency;
    partsCostCents: number;
    laborCostCents: number;
    laborHours?: number;
  }) => Promise<void>;
  item: {
    id: string;
    name: string;
    categoryId: string;
    condition: 'good' | 'fair' | 'poor' | 'critical' | null;
  };
  vehicleMake?: string;
  vehicleModel?: string;
  isLoading?: boolean;
  recommendation?: InspectionRecommendation;
}

export function AddRecommendationDialog({
  open,
  onClose,
  onSave,
  item,
  vehicleMake,
  vehicleModel,
  isLoading = false,
  recommendation,
}: AddRecommendationDialogProps) {
  const { t } = useTranslation('inspection');
  const theme = useTheme();
  const estimateCost = useEstimateCost();
  const { isDirty, markDirty } = useFormDirty(open);

  const urgencyOptions: { value: RecommendationUrgency; label: string; color: string }[] = [
    { value: 'immediate', label: t('recommendation.urgent'), color: theme.palette.error.main },
    { value: 'soon', label: t('recommendation.soon'), color: theme.palette.warning.main },
    { value: 'future', label: t('recommendation.future'), color: theme.palette.success.main },
  ];

  const [description, setDescription] = useState('');
  const [customerDescription, setCustomerDescription] = useState('');
  const [urgency, setUrgency] = useState<RecommendationUrgency>('soon');
  const [partsCostCents, setPartsCostCents] = useState(0);
  const [laborCostCents, setLaborCostCents] = useState(0);
  const [laborHours, setLaborMinutes] = useState(30);
  const [error, setError] = useState<string | null>(null);
  const [hasEstimate, setHasEstimate] = useState(false);

  const priceResetKey = open ? recommendation?.id ?? item.id : 'closed';
  const partsCostInput = useCentsInput(partsCostCents, (cents) => {
    setPartsCostCents(cents);
    markDirty();
  }, priceResetKey);
  const laborCostInput = useCentsInput(laborCostCents, (cents) => {
    setLaborCostCents(cents);
    markDirty();
  }, priceResetKey);

  useEffect(() => {
    if (open) {
      setDescription(recommendation?.description ?? '');
      setCustomerDescription(recommendation?.customerDescription ?? '');
      setUrgency(recommendation?.urgency ?? 'soon');
      setPartsCostCents(recommendation?.partsCostCents ?? 0);
      setLaborCostCents(recommendation?.laborCostCents ?? 0);
      setLaborMinutes(Number(recommendation?.laborHours ?? (recommendation ? 0 : 30)));
      setError(null);
      setHasEstimate(false);

      if (!recommendation && item.condition && item.condition !== 'good') {
        void fetchEstimate();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item.categoryId, item.id, item.condition, recommendation?.id]);

  const fetchEstimate = async () => {
    if (!item.condition || item.condition === 'good') return;

    try {
      const result = await estimateCost.mutateAsync({
        categoryId: item.categoryId,
        itemId: item.id,
        condition: item.condition,
        vehicleMake,
        vehicleModel,
      });

      const estimate: CostEstimate = result.data;

      setDescription(estimate.description);
      setCustomerDescription(estimate.customerDescription);
      setUrgency(estimate.urgency);
      setPartsCostCents(estimate.partsSellCents);
      setLaborCostCents(estimate.laborCostCents);
      setLaborMinutes(estimate.labor.minutes);
      setHasEstimate(true);
    } catch {
      setHasEstimate(false);
    }
  };

  const handleSave = async () => {
    setError(null);

    if (!description.trim()) {
      setError(t('recommendation.descriptionRequired'));
      return;
    }

    try {
      const nextPartsCostCents = partsCostInput.commit(partsCostInput.value);
      const nextLaborCostCents = laborCostInput.commit(laborCostInput.value);
      await onSave({
        description: description.trim(),
        customerDescription: customerDescription.trim() || (recommendation ? '' : undefined),
        urgency,
        partsCostCents: nextPartsCostCents,
        laborCostCents: nextLaborCostCents,
        laborHours,
      });
      onClose();
    } catch {
      setError(t('recommendation.saveFailed'));
    }
  };

  const subtotalCents = partsCostCents + laborCostCents;
  // Preview at 25.5% so it matches what the server stores (staff-api computes
  // the saved recommendation at 25.5%, not the legacy 24%) — #1031.
  const vatCents = vatCentsFromNet(subtotalCents);
  const totalCents = subtotalCents + vatCents;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={recommendation ? t('recommendation.edit') : t('recommendation.title', { name: item.name })}
      headerIcon={<WarningIcon color="warning" />}
      isDirty={isDirty}
      onSubmit={() => void handleSave()}
      submitLabel={t('recommendation.save')}
      submitDisabled={!description.trim()}
      isLoading={isLoading}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {estimateCost.isPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t('recommendation.fetchingEstimate')}
            </Typography>
          </Box>
        )}

        {hasEstimate && (
          <Alert severity="info" icon={<AutoFixHighIcon />} sx={{ mb: 2 }}>
            {t('recommendation.estimateAutoFilled')}
          </Alert>
        )}

        <Stack spacing={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('recommendation.urgency')}</InputLabel>
            <Select
              value={urgency}
              label={t('recommendation.urgency')}
              onChange={(e) => { setUrgency(e.target.value as RecommendationUrgency); markDirty(); }}
            >
              {urgencyOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: opt.color,
                      }}
                    />
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('recommendation.internalDescription')}
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            fullWidth
            required
            autoFocus
            multiline
            rows={2}
            placeholder={t('recommendation.internalDescriptionPlaceholder')}
            helperText={t('recommendation.visibleToStaffOnly')}
          />

          <TextField
            label={t('recommendation.customerDescription')}
            value={customerDescription}
            onChange={(e) => { setCustomerDescription(e.target.value); markDirty(); }}
            fullWidth
            multiline
            rows={2}
            placeholder={t('recommendation.customerDescriptionPlaceholder')}
            helperText={t('recommendation.visibleToCustomer')}
          />

          <Divider>
            <Chip
              icon={<CalculateIcon />}
              label={t('recommendation.costEstimate')}
              size="small"
              variant="outlined"
            />
          </Divider>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('recommendation.parts')}
              type="number"
              size="small"
              value={partsCostInput.value}
              onChange={partsCostInput.onChange}
              onFocus={partsCostInput.onFocus}
              onBlur={partsCostInput.onBlur}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              inputProps={{ step: '0.01', min: '0' }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('recommendation.labor')}
              type="number"
              size="small"
              value={laborCostInput.value}
              onChange={laborCostInput.onChange}
              onFocus={laborCostInput.onFocus}
              onBlur={laborCostInput.onBlur}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
              inputProps={{ step: '0.01', min: '0' }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('recommendation.time')}
              type="number"
              size="small"
              value={laborHours}
              onChange={(e) => { setLaborMinutes(parseInt(e.target.value, 10) || 0); markDirty(); }}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
              inputProps={{ step: '5', min: '0' }}
              sx={{ width: 120 }}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('recommendation.subtotal')}
              </Typography>
              <Typography variant="body2">€{formatCentsAsEuros(subtotalCents)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('recommendation.vat')}
              </Typography>
              <Typography variant="body2">€{formatCentsAsEuros(vatCents)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('recommendation.total')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={600} color="primary.main">
                €{formatCentsAsEuros(totalCents)}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
